<template>
  <div class="project-roadblock">
    <b-skeleton-wrapper :loading="initialLoading">
      <template #loading>
        <div class="mt-5 mb-2">
          <b-skeleton
            type="input"
            class="w-100"
          />
        </div>

        <b-row>
          <b-col
            v-for="i in IS_MOBILE() ? 2 : 6"
            :key="i"
            md="4"
            class="mb-2"
          >
            <b-card>
              <b-skeleton class="w-25" />
              <b-skeleton class="w-75" />
              <b-skeleton class="w-100" />

              <div class="avatar-group">
                <b-skeleton type="avatar" />
                <b-skeleton type="avatar" />
                <b-skeleton type="avatar" />
              </div>
            </b-card>
          </b-col>
        </b-row>
      </template>

      <div v-if="total || isOwner || company || isAdmin">
        <!-- Header Section -->
        <div class="roadblock-header">
          <div class="d-flex align-items-start">
            <div class="header-icon btn-primary mr-2">
              <feather-icon
                icon="GridIcon"
                size="24"
              />
            </div>
            <div>
              <h2 class="mb-0">
                {{ $t('Select a Project') }}
              </h2>
              <p class="text-muted mb-0">
                {{ $t('projects.showing-project-length', {
                  count: filteredProjects.length, total: searchTotal ?
                    searchTotal :
                    total
                }) }}
              </p>
            </div>
          </div>

          <div class="header-actions d-flex align-items-center">
            <div class="view-toggle mr-2">
              <b-button
                :variant="viewMode === 'grid' ? 'primary' : 'outline-secondary'"
                size="sm"
                class="btn-icon"
                @click="viewMode = 'grid'"
              >
                <feather-icon
                  icon="GridIcon"
                  size="16"
                />
              </b-button>
              <b-button
                :variant="viewMode === 'list' ? 'primary' : 'outline-secondary'"
                size="sm"
                class="btn-icon ml-50"
                @click="viewMode = 'list'"
              >
                <feather-icon
                  icon="ListIcon"
                  size="16"
                />
              </b-button>
            </div>
          </div>
        </div>

        <!-- Search and New Project Row -->
        <b-row class="search-row align-items-center mb-2">
          <b-col
            class="search-input-wrapper"
            cols="8"
            md="10"
          >
            <feather-icon
              icon="SearchIcon"
              size="18"
              class="search-icon"
            />
            <b-form-input
              v-model="term"
              :placeholder="$t('Search projects...')"
              class="search-input"
              @input="onSearch"
            />
          </b-col>
          <b-col
            cols="4"
            md="2"
          >
            <b-button-group v-if="company || isOwner || isAdmin">
              <b-button
                v-if="company || isOwner"
                v-b-modal.create-project-modal
                variant="primary"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                />
                Project
              </b-button>

              <b-button
                v-if="isAdmin"
                v-b-modal.create-company-modal
                variant="primary"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                />
                Company
              </b-button>
            </b-button-group>
          </b-col>
        </b-row>

        <!-- Projects Grid/List -->
        <b-overlay
          :show="isLoading"
          rounded="sm"
        >
          <div v-if="filteredProjects.length">
            <!-- Grid View -->
            <b-row v-if="viewMode === 'grid'">
              <b-col
                v-for="(project, index) in filteredProjects"
                :key="project.uuid"
                md="4"
                sm="6"
                class="mb-2"
              >
                <b-card
                  class="project-card mb-0"
                  :class="`border-color-${getColorIndex(index)}`"
                  role="button"
                  @click="updateSelectedProject(project.uuid, project)"
                >
                  <div class="project-info">
                    <h5 class="project-name mb-50">
                      {{ project.name }}
                    </h5>
                    <p class="company-name text-muted mb-0">
                      {{ project.company.name }}
                    </p>
                  </div>
                  <div class="project-footer d-flex justify-content-between align-items-center mt-2">
                    <div class="avatar-group">
                      <b-avatar
                        v-for="(user, userIndex) in project.users.data.slice(0, 3)"
                        :key="user.uuid"
                        :src="getAvatar(user.avatar)"
                        :text="getInitials(user.name)"
                        size="32"
                        :variant="getAvatarVariant(index, userIndex)"
                        class="avatar-item"
                      />
                      <b-avatar
                        v-if="project.users.total > 3"
                        size="32"
                        variant="secondary"
                        class="avatar-item avatar-more"
                      >
                        <span class="small">+{{ project.users.total - 3 }}</span>
                      </b-avatar>
                    </div>
                    <div class="member-count text-muted">
                      <feather-icon
                        icon="UsersIcon"
                        size="14"
                        class="mr-50"
                      />
                      <span>{{ project.users.total }}</span>
                    </div>
                  </div>
                </b-card>
              </b-col>
            </b-row>

            <!-- List View -->
            <div
              v-else
              class="list-view"
            >
              <b-card
                v-for="(project, index) in filteredProjects"
                :key="project.uuid"
                class="project-list-item mb-1"
                :class="`border-left-color-${getColorIndex(index)}`"
                no-body
                role="button"
                @click="updateSelectedProject(project.uuid, project)"
              >
                <div class="d-flex align-items-center justify-content-between p-1">
                  <div class="project-info">
                    <h6 class="project-name mb-0">
                      {{ project.name }}
                    </h6>
                    <small class="company-name text-muted">{{ project.company.name }}</small>
                  </div>
                  <div class="d-flex align-items-center">
                    <div class="avatar-group mr-2">
                      <b-avatar
                        v-for="(user, userIndex) in project.users.data.slice(0, 3)"
                        :key="user.uuid"
                        :src="getAvatar(user.avatar)"
                        :text="getInitials(user.name)"
                        size="28"
                        :variant="getAvatarVariant(index, userIndex)"
                        class="avatar-item"
                      />
                      <b-avatar
                        v-if="project.users.total > 3"
                        size="28"
                        variant="light-secondary"
                        class="avatar-item avatar-more"
                      >
                        <span class="small">+{{ project.users.total - 3 }}</span>
                      </b-avatar>
                    </div>
                    <div class="member-count text-muted">
                      <feather-icon
                        icon="UsersIcon"
                        size="14"
                        class="mr-50"
                      />
                      <span>{{ project.users.total }}</span>
                    </div>
                  </div>
                </div>
              </b-card>
            </div>

            <!-- Load More -->
            <div
              v-if="hasMore"
              class="text-center mt-2"
            >
              <b-button
                variant="outline-primary"
                @click="loadMore"
              >
                {{ $t('Load More') }}
              </b-button>
            </div>
          </div>

          <!-- No Projects Found -->
          <b-card
            v-else
            class="text-center py-3"
          >
            <feather-icon
              icon="FolderIcon"
              size="48"
              class="text-muted mb-1"
            />
            <h4>{{ $t('projects.no-projects') }}</h4>
            <p class="text-muted">
              {{ term ? $t('No projects match your search') : $t('Get started by creating a new project') }}
            </p>
            <b-button
              v-if="isOwner && !term"
              v-b-modal.create-project-modal
              variant="primary"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
                class="mr-50"
              />
              {{ $t('Create Project') }}
            </b-button>
          </b-card>
        </b-overlay>
      </div>

      <!-- No Access Alert -->
      <b-alert
        v-else-if="!(isLoading || initialLoading)"
        variant="danger"
        show
        class="p-2 text-center"
      >
        <feather-icon
          icon="FrownIcon"
          size="72"
          class="mb-1"
        />
        <h1 class="text-danger">
          {{ $t('Oops!') }}
        </h1>
        <p>{{ $t('projects.deactivated') }}</p>
        <p>{{ $t('projects.access-info') }}</p>
      </b-alert>

      <!-- Create Project Modal -->
      <create-edit-project
        v-if="company || isOwner || isAdmin"
        :company-id="company ? company.uuid : null"
        :owner="isOwner ? self.uuid : null"
        :is-admin="isAdmin"
        @onSave="fetchProjects('initial', false)"
      />

      <!-- Create Company Modal -->
      <b-modal
        v-if="company || isOwner || isAdmin"
        id="create-company-modal"
        :title="$t('Create a new company')"
        size="lg"
        hide-footer
        no-close-on-backdrop
      >
        <company-form
          @onSave="$bvModal.hide('create-company-modal')"
          @cancel="$bvModal.hide('create-company-modal')"
        />
      </b-modal>
    </b-skeleton-wrapper>
  </div>
</template>
<script>
import {
  BCard, BFormInput, BOverlay, BAlert, BSkeletonWrapper, BSkeleton, BModal, BButtonGroup, BButton, BRow, BCol, BAvatar,
} from 'bootstrap-vue'
import useApollo from '@/plugins/graphql/useApollo'
import { getUserData } from '@/auth/utils'
import { checkPermission, projectSettings } from '@/utils/permissions'
import CreateEditProject from '@/views/Settings/company/projects/createEdit.vue'
import _ from 'lodash'
import CompanyForm from '@/views/Settings/company/main/CompanyForm.vue'
import { continueTour, pauseTour, setComponentReady } from '@/views/tour/tour'
import { ownerTourIndexes } from '@/views/tour/tourConstants'
import { mapPermissionSlug } from '@/const/common'
import { mapGetters } from 'vuex'

const borderColors = ['purple', 'blue', 'teal', 'orange', 'pink', 'cyan']
const avatarVariants = ['primary', 'success', 'info', 'warning', 'danger', 'secondary']

export default {
  components: {
    BCard,
    BFormInput,
    BOverlay,
    BSkeletonWrapper,
    BSkeleton,
    BAlert,
    BModal,
    BButton,
    BButtonGroup,
    BRow,
    BCol,
    BAvatar,
    CreateEditProject,
    CompanyForm,
  },
  data() {
    return {
      pauseTour,
      self: getUserData(),
      term: '',
      projects: [],
      allProjects: [],
      currentPage: 1,
      total: 0,
      searchTotal: 0,
      perPage: 12,
      isLoading: false,
      initialLoading: true,
      viewMode: 'grid',
      company: null,
    }
  },
  computed: {
    ...mapGetters('permissions', ['getRequiredPermissionsForNavbar']),
    filteredProjects() {
      return this.projects
    },
    isOwner() {
      return this.self.companyRoles?.data.filter(companyRole => companyRole.role.name === 'owner').length
    },
    isAdmin() {
      return this.self.isAdmin
    },
    hasMore() {
      const totalToCheck = this.term ? this.searchTotal : this.total
      return this.projects.length < totalToCheck
    },
  },
  mounted() {
    if (this.self) this.fetchProjects('initial')
    this.$store.dispatch('project/setSelectedUser', null)
  },
  methods: {
    getColorIndex(index) {
      return borderColors[index % borderColors.length]
    },
    getAvatarVariant(projectIndex, userIndex) {
      return avatarVariants[(projectIndex + userIndex) % avatarVariants.length]
    },
    loadMore() {
      this.currentPage += 1
      if (this.term !== '') this.search()
      else this.fetchProjects()
    },
    fetchProjects(type = '', stack = true) {
      const { projects } = this
      if (type === 'initial') this.initialLoading = true
      else this.isLoading = true

      useApollo.project.getProjects({ first: this.perPage, page: this.currentPage, includeInactive: false }).then(response => {
        if (type === 'initial') this.allProjects = response.data?.me?.projects?.data || []
        this.total = response.data?.me?.projects?.total || 0
        this.$store.dispatch('tour/setProjectCount', this.total)

        if (stack && type !== 'initial') {
          projects.push(...(response.data?.me?.projects?.data || []))
          this.projects = projects
        } else {
          this.projects = [...this.allProjects]
        }

        if (!this.total) this.getMyCompanies()
      }).catch(error => {
        this.showErrorMessage(error)
      }).finally(() => {
        this.initialLoading = false
        this.isLoading = false
        this.$nextTick(() => {
          setComponentReady()
          if (this.$store.state.tour.isTouringPaused) continueTour({ step: ownerTourIndexes.TOUR_SELECT_A_PROJECT })
        })
      })
    },
    onSearch: _.debounce(function () {
      // this.projects = []
      this.currentPage = 1
      this.$nextTick(() => this.search())
    }, 500),
    search() {
      if (!this.term.trim()) {
        this.searchTotal = 0
        this.$nextTick(() => {
          this.projects = [...this.allProjects]
        })
        return
      }
      this.isLoading = true
      useApollo.project.searchProjects({
        keyword: this.term, first: this.perPage, page: this.currentPage,
      }).then(response => {
        const { projects } = this
        if (this.currentPage === 1) {
          this.projects = response.data?.me?.projects?.data || []
        } else {
          projects.push(...(response.data?.me?.projects?.data || []))
          this.projects = projects
        }
        this.searchTotal = response.data?.me?.projects?.total ?? 0
      }).finally(() => { this.isLoading = false })
    },
    updateSelectedProject(value, project = null) {
      pauseTour()
      this.isLoading = true
      if (!project) project = this.projects.find(p => p.uuid === value)
      this.$store.dispatch('project/SET_PROJECT_BASIC_REQUIREMENT', {
        project,
        value,
      }).then(() => {
        checkPermission(this.getRequiredPermissionsForNavbar, permissions => {
          const userPermissions = Object.entries(permissions).map(([key, val]) => ({ key: mapPermissionSlug[key], value: val }))
          this.$store.dispatch('permissions/setPermissions', userPermissions)
        })
        projectSettings().then(response => {
          this.$store.dispatch('project/setProjectSettings', {
            enableCloudService: response?.enableCloudService,
          })
        })
      }).finally(() => {
        this.$router.replace({ name: 'home' })
      })
    },
    getMyCompanies() {
      useApollo.users.getMyCompanies().then(response => {
        this.company = response.data.me.companyRoles?.data[0]?.company
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.project-roadblock {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  .roadblock-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;

    .header-icon {
      width: 48px;
      height: 48px;
      background: rgba(115, 103, 240, 0.12);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
    }

    .view-toggle {
      .btn {
        padding: 0.5rem;
      }
    }
  }

  .search-row {
    .search-input-wrapper {
      position: relative;

      .search-icon {
        position: absolute;
        left: 2rem;
        top: 50%;
        transform: translateY(-50%);
        color: #b4b7bd;
      }

      .search-input {
        padding-left: 2.75rem;
        height: 44px;
        border-radius: 8px;
        border: 1px solid #ebe9f1;
        background: #fff;

        &:focus {
          border-color: #3D61AD;
          box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
        }
      }
    }

    .new-project-btn {
      white-space: nowrap;
      height: 44px;
      border-radius: 8px;
      padding: 0 1.25rem;
    }
  }

  .project-card {
    border-radius: 8px;
    border: none;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    transition: all 0.3s ease;
    border-top: 3px solid transparent;
    cursor: pointer;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 4px 25px 0 rgba(34, 41, 47, 0.25);
    }

    &.border-color-purple {
      border-top-color: #7367f0;
    }

    &.border-color-blue {
      border-top-color: #00cfe8;
    }

    &.border-color-teal {
      border-top-color: #28c76f;
    }

    &.border-color-orange {
      border-top-color: #ff9f43;
    }

    &.border-color-pink {
      border-top-color: #ea5455;
    }

    &.border-color-cyan {
      border-top-color: #00cfe8;
    }

    .project-name {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.25rem;
    }

    .company-name {
      font-size: 0.875rem;
    }

    .avatar-group {
      display: flex;

      .avatar-item {
        margin-left: -8px;
        border: 2px solid #fff;

        &:first-child {
          margin-left: 0;
        }
      }

      .avatar-more {
        font-size: 0.75rem;
      }
    }

    .member-count {
      font-size: 0.875rem;
      display: flex;
      align-items: center;
    }
  }

  .project-list-item {
    border-radius: 8px;
    border: none;
    box-shadow: 0 2px 12px 0 rgba(34, 41, 47, 0.08);
    transition: all 0.2s ease;
    border-left: 4px solid transparent;
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 20px 0 rgba(34, 41, 47, 0.15);
    }

    &.border-left-color-purple {
      border-left-color: #7367f0;
    }

    &.border-left-color-blue {
      border-left-color: #00cfe8;
    }

    &.border-left-color-teal {
      border-left-color: #28c76f;
    }

    &.border-left-color-orange {
      border-left-color: #ff9f43;
    }

    &.border-left-color-pink {
      border-left-color: #ea5455;
    }

    &.border-left-color-cyan {
      border-left-color: #00cfe8;
    }

    .avatar-group {
      display: flex;

      .avatar-item {
        margin-left: -6px;
        border: 2px solid #fff;

        &:first-child {
          margin-left: 0;
        }
      }
    }
  }
}

.dark-layout {
  .project-roadblock {
    .search-input-wrapper .search-input {
      background: #283046;
      border-color: #3b4253;
    }

    .project-card {
      background: #283046;
      box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.24);

      .project-name {
        color: #d0d2d6;
      }

      .avatar-item {
        border-color: #283046;
      }
    }

    .project-list-item {
      background: #283046;

      .avatar-item {
        border-color: #283046;
      }
    }
  }
}

@media (max-width: 768px) {
  .project-roadblock {
    padding: 1rem;

    .roadblock-header {
      flex-direction: column;
      gap: 1rem;

      .header-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }
  }
}
</style>
